import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useEffect, useMemo, useRef} from 'react';

export interface ShallowRenderingState {
  /**
   * setShallowRenderingState updates the internal states for making shallow rendering possible, mainly:
   * 1) state of product tiles which come into view => currentVisibleItems
   * 2) the product tiles which should be rendered on history back => lastVisibleItems
   */
  setShallowRenderingState: (config: {
    visibilityId: string;
    height?: number;
    inView: boolean;
  }) => void;

  /**
   * shouldShallowRenderTile determines if a tile should be rendered shallowly.
   * check inline comments for more details.
   */
  shouldShallowRenderTile: (config: {
    visibilityId: string;
    inView: boolean;
  }) => boolean;

  /**
   * This function determines whether the tile is visible or not on initial render.
   * We render the tile immediately if the was visible on the last page.
   * The tiles visible on the last page are stored in lastVisibleItems Map.
   * NOTE: we always render the first few tiles which has priority high|medium to avoid flickering on page load
   */
  shouldBeVisibleOnInitialLoad: (config: {visibilityId: string}) => boolean;

  /**
   * Access the former tile height e.g. to create a wrapper component
   */
  getTileHeight: (visibilityId: string) => number | undefined;
}

export const useShallowRenderingState = ({
  uniqueKey,
}: {
  uniqueKey: string;
}): ShallowRenderingState => {
  const [lastVisibleItems, setLastVisibleItems] = useHistoryPageState(
    new Map<string, {inView: boolean; height: number | undefined}>(),
    {
      uniqueKey,
    },
  );

  // currentVisibleItems tracks tiles that already ran through the current page lifecycle.
  // Once a tile was processed, it should not switch to shallow rendering during the
  // same mount, otherwise repeated `inView=false` callbacks can cause flicker.
  const currentVisibleItems = useRef(new Set<string>()).current;

  // need to update the lastVisibleItems state when unmounting since we need the updated state only on history back.
  useEffect(
    () => () => {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setLastVisibleItems(new Map(lastVisibleItems));
    },
    [],
  );

  const shouldBeVisibleOnInitialLoad = ({
    visibilityId,
  }: {
    visibilityId: string;
  }) => {
    return lastVisibleItems.get(visibilityId)?.inView === true;
  };

  const shouldShallowRenderTile = ({
    visibilityId,
    inView,
  }: {
    visibilityId: string;
    inView: boolean;
  }) => {
    // should render product tile shallow when:
    return (
      inView === false && // => the tile is not in view
      currentVisibleItems.has(visibilityId) === false && // => and the tile was not already scrolled to.
      lastVisibleItems.get(visibilityId)?.inView === false // => and the tile was not visible on the last page viewport.
    );
  };

  const getTileHeight = (visibilityId: string) =>
    lastVisibleItems.get(visibilityId)?.height;

  const setShallowRenderingState = ({
    visibilityId,
    height,
    inView,
  }: {
    visibilityId: string;
    height?: number;
    inView: boolean;
  }) => {
    currentVisibleItems.add(visibilityId);

    lastVisibleItems.set(visibilityId, {inView, height});
  };

  return useMemo(
    () => ({
      setShallowRenderingState,
      shouldBeVisibleOnInitialLoad,
      shouldShallowRenderTile,
      getTileHeight,
    }),
    // intentionally not including the functions here in the dependencies,
    // since we dont need to re-render if the functions change as the functions are only setting state which will be used on history back.
    [],
  );
};
